import React, { useState, useMemo, useCallback } from 'react';

interface CalculationResultsProps {
  results: {
    actualEstimatedRevenue: number;
    estimatedGrossMargin: number;
    monthlyProfitNoDepreciation: number;
    monthlyProfitWithDepreciation: number;
    paybackPeriod: number;
    rent36Month: number;
    minRevenueFor36MonthPayback: number;
  };
}

// 性能优化：使用React.memo包装组件，避免不必要的重渲染
const CalculationResults: React.FC<CalculationResultsProps> = React.memo(({ results }) => {
  // 添加折叠状态，默认为展开
  const [isCollapsed, setIsCollapsed] = useState(false);

  // 性能优化：使用useMemo缓存格式化函数
  const formatCurrency = useMemo(() => {
    return (value: number): string => {
      return Math.round(value).toLocaleString('zh-CN');
    };
  }, []);

  // 性能优化：使用useMemo缓存回本周期状态计算
  const paybackStatus = useMemo(() => {
    const months = results.paybackPeriod;
    if (months <= 12) {
      return { color: 'text-green-700', text: '优秀' };
    } else if (months <= 24) {
      return { color: 'text-blue-700', text: '良好' };
    } else if (months <= 36) {
      return { color: 'text-yellow-700', text: '一般' };
    } else {
      return { color: 'text-red-700', text: '较长' };
    }
  }, [results.paybackPeriod]);

  // 性能优化：使用useCallback缓存切换折叠状态函数
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  return (
    <div className="mb-2 animate-fade-in">
      {/* 标题和折叠按钮放在同一行 */}
      <div className="flex justify-between items-center mb-1 cursor-pointer" onClick={toggleCollapse}>
        <h2 className="section-title title-calculation-results mb-0 w-full">测算结果</h2>
      </div>
      
      {!isCollapsed && (
        <div className="animate-fade-in">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">预估月营业额</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(results.actualEstimatedRevenue)}
              </div>
              <div className="mt-1 text-xs text-gray-600">实际使用的预估月营业额</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">预估毛利</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(results.estimatedGrossMargin)}<span className="text-xs font-normal">/月</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">扣除成本后的销售收入</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">月均利润（不含折旧）</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(results.monthlyProfitNoDepreciation)}<span className="text-xs font-normal">/月</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">不考虑设备折旧的月度实际盈利</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">回本周期</div>
              <div className="flex items-baseline">
                <span className="result-value text-gray-800 text-base md:text-lg font-medium">
                  {results.paybackPeriod.toFixed(1)}
                </span>
                <span className="text-xs font-normal text-gray-600 ml-1">个月</span>
                <span className={`ml-1 text-xs font-medium ${paybackStatus.color} bg-white bg-opacity-70 px-1 py-0.5 rounded-full`}>
                  {paybackStatus.text}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">收回全部投资所需的时间</div>
            </div>
            
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">36个月回本租金</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(results.rent36Month)}<span className="text-xs font-normal">/月</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">36个月内回本的最高可接受租金</div>
            </div>
            
            {/* 新增结果卡片：36个月回本所需最低月营业额 */}
            <div className="result-card p-2" style={{backgroundColor: "#F8FAFC"}}>
              <div className="result-label text-xs mb-0 text-gray-700">36个月回本所需营业额</div>
              <div className="result-value text-gray-800 text-base md:text-lg font-medium">
                ¥{formatCurrency(results.minRevenueFor36MonthPayback)}<span className="text-xs font-normal">/月</span>
              </div>
              <div className="mt-1 text-xs text-gray-600">36个月内收回投资的最低月营业额</div>
            </div>
          </div>
          
          <div className="mt-1 rounded-lg p-2 flex items-start">
            <svg className="w-4 h-4 text-yellow-500 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            <p className="text-xs text-gray-400">
              注意：以上测算结果仅供参考，实际经营情况可能受多种因素影响而有所不同。建议在做出投资决策前，结合当地市场环境、消费习惯、竞争情况等因素进行综合评估。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}); // React.memo结束

export default CalculationResults;